// src/pages/AdminLoginPage.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  Stack,
} from "@mui/material";
import {
  Tune as CenterIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { loginAsAdmin } from "../app/auth";

interface AdminLoginPageProps {
  onLoginSuccess: () => void;
  onNavigateToDrive: () => void;
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onloadTurnstileCallback?: () => void;
  }
}

declare const process: any;

export const AdminLoginPage: React.FC<AdminLoginPageProps> = ({
  onLoginSuccess,
  onNavigateToDrive,
}) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cloudflare Turnstile 人机验证状态
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string>("");
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);

  // 1. 获取 Turnstile 站点密钥 (优先使用前端环境变量，其次请求服务端配置)
  useEffect(() => {
    let isMounted = true;
    const loadSiteKey = async () => {
      const buildTimeKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
      if (buildTimeKey) {
        if (isMounted) setTurnstileSiteKey(buildTimeKey);
        return;
      }
      try {
        const res = await fetch("/api/center/turnstile-config");
        if (res.ok) {
          const data: any = await res.json();
          if (isMounted && data?.siteKey) {
            setTurnstileSiteKey(data.siteKey);
          }
        }
      } catch (err) {
        console.warn("Failed to load Turnstile config from server:", err);
      }
    };
    loadSiteKey();
    return () => {
      isMounted = false;
    };
  }, []);

  // 2. 当检测到 siteKey 时动态注入 Cloudflare Turnstile 脚本并渲染挂件
  useEffect(() => {
    if (!turnstileSiteKey) return;

    let widgetId: string | null = null;
    let timerId: ReturnType<typeof setInterval> | null = null;

    const renderWidget = () => {
      if (!window.turnstile || !turnstileContainerRef.current) return;
      if (turnstileWidgetIdRef.current) return; // 已渲染

      try {
        widgetId = window.turnstile.render(turnstileContainerRef.current, {
          sitekey: turnstileSiteKey,
          theme: "dark",
          size: "normal",
          callback: (token: string) => {
            setTurnstileToken(token);
            setError(null);
          },
          "expired-callback": () => {
            setTurnstileToken("");
          },
          "error-callback": () => {
            setTurnstileToken("");
          },
        });
        turnstileWidgetIdRef.current = widgetId;
      } catch (err) {
        console.error("Failed to render Turnstile widget:", err);
      }
    };

    // 检查脚本是否已存在
    const SCRIPT_ID = "cf-turnstile-script";
    let scriptTag = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.id = SCRIPT_ID;
      scriptTag.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      scriptTag.async = true;
      scriptTag.defer = true;
      scriptTag.onload = () => {
        renderWidget();
      };
      document.head.appendChild(scriptTag);
    } else if (window.turnstile) {
      renderWidget();
    } else {
      // 脚本已插入但尚未完成加载
      timerId = setInterval(() => {
        if (window.turnstile) {
          if (timerId) clearInterval(timerId);
          renderWidget();
        }
      }, 100);
    }

    return () => {
      if (timerId) clearInterval(timerId);
      if (turnstileWidgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        } catch {
          // ignore
        }
        turnstileWidgetIdRef.current = null;
      }
    };
  }, [turnstileSiteKey]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError("请输入用户名和密码");
      return;
    }

    if (turnstileSiteKey && !turnstileToken) {
      setError("请先完成人机验证");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await loginAsAdmin(username, password, turnstileToken);
      onLoginSuccess();
    } catch (err: any) {
      setError(err?.message || "登录失败，请检查账号密码");
      // 重置人机验证
      if (turnstileWidgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.reset(turnstileWidgetIdRef.current);
          setTurnstileToken("");
        } catch {
          // ignore
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        backgroundImage: "url(/bg-dark.webp)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
      }}
    >
      <Card
        sx={{
          maxWidth: 440,
          width: "100%",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          backgroundColor: "rgba(20, 22, 36, 0.85)",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          borderRadius: 4,
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.6)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, rgba(107, 124, 255, 0.25) 0%, rgba(138, 92, 246, 0.15) 100%)",
            p: 3,
            textAlign: "center",
            borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              p: 1.5,
              borderRadius: "50%",
              backgroundColor: "rgba(107, 124, 255, 0.15)",
              color: "#8a99ff",
              mb: 1.5,
              boxShadow: "0 0 20px rgba(107, 124, 255, 0.3)",
            }}
          >
            <CenterIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography variant="h5" fontWeight="bold" sx={{ color: "#fff", letterSpacing: 0.5 }}>
            FlareDrive-Pro
          </Typography>
          <Typography variant="subtitle2" sx={{ color: "rgba(255, 255, 255, 0.7)", mt: 0.5 }}>
            控制中心
          </Typography>
        </Box>

        <CardContent sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="用户名"
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255, 255, 255, 0.04)",
                  },
                }}
              />

              <TextField
                fullWidth
                label="密码"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "rgba(255, 255, 255, 0.5)" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: "rgba(255, 255, 255, 0.5)" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "rgba(255, 255, 255, 0.04)",
                  },
                }}
              />

              {/* Cloudflare Turnstile 人机验证挂件挂载点 */}
              {turnstileSiteKey && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    minHeight: 65,
                    my: 0.5,
                  }}
                >
                  <div ref={turnstileContainerRef} />
                </Box>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 1,
                  py: 1.4,
                  borderRadius: 2,
                  fontSize: "1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  background: "linear-gradient(135deg, #6b7cff 0%, #8a5cf6 100%)",
                  boxShadow: "0 8px 24px rgba(107, 124, 255, 0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #5b6cee 0%, #7a4ce6 100%)",
                    boxShadow: "0 8px 30px rgba(107, 124, 255, 0.5)",
                  },
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "登 录"}
              </Button>

              <Box sx={{ textAlign: "center", pt: 1 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={onNavigateToDrive}
                  sx={{
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: "0.875rem",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    textDecoration: "none",
                    "&:hover": {
                      color: "#6b7cff",
                    },
                  }}
                >
                  <ArrowBackIcon fontSize="small" /> 返回网盘
                </Link>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminLoginPage;
